import React from 'react';
import { colors } from '@config/colors';
import { clamp } from './timeline';

/*
 * The wish story's people, in the design lab's sumi-ink style: gestural
 * brush figures in the same hand as the logo's house, blank faces, solid
 * ink hair, over a soft wash of the family's colour. Each pose is drawn
 * three times — sad, neutral, happy — with identical path structure, and
 * mood blends between them, so a mom's shoulders drop or lift and a kid's
 * arms go up as the answer she gets gets better.
 */

const INK = colors.neutral.extraDark;
const WASH = 0.42;

/** Each family's wash: the mom's, then her kids'. Maya's family wears the
 *  cool accents — zen, blue, teal — and the happiest family the most
 *  colour — purple, magenta, pink (June, 2026-09-23). */
const FAMILIES = {
  1: { mom: colors.accent.zen.base, kids: [colors.accent.blue.dark, colors.accent.teal.extraDark, colors.accent.zen.light] },
  2: { mom: colors.accent.coral.dark, kids: [colors.accent.coral.extraLight, colors.brand.extraDark, colors.accent.yellow.dark] },
  3: { mom: colors.accent.purple.dark, kids: [colors.accent.magenta.base, colors.accent.pink.dark, colors.accent.purple.base] },
} as const;

export type Family = keyof typeof FAMILIES;

/** The happiest family's balloons, one per kid. */
const BALLOONS = [colors.accent.pink.extraDark, colors.accent.magenta.dark, colors.accent.purple.dark] as const;

const NUM = /-?\d+\.?\d*/g;

/** Blend two paths with the same commands, number by number. */
const lerpPath = (a: string, b: string, t: number): string => {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const from = a.match(NUM) ?? [];
  const to = b.match(NUM) ?? [];
  let i = 0;
  return a.replace(NUM, () => {
    const v = +from[i] + (+to[i] - +from[i]) * t;
    i++;
    return v.toFixed(2);
  });
};

/** The pose for a mood: neutral, leaning toward sad or toward happy. */
const posed = (sad: string, neutral: string, happy: string, sadness: number, joy: number) =>
  sadness > 0 ? lerpPath(neutral, sad, sadness) : lerpPath(neutral, happy, joy);

const endPoint = (d: string): [number, number] => {
  const nums = (d.match(NUM) ?? []).map(Number);
  return [nums[nums.length - 2], nums[nums.length - 1]];
};

const Stroke: React.FC<{ d: string; w: number; o?: number }> = ({ d, w, o = 1 }) => (
  <path d={d} stroke={INK} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" fill="none" opacity={o} />
);

type BodyProps = {
  s: number;
  tint: string;
  /** 0 glum … 0.6 neutral … 1.2 delighted. */
  mood: number;
  swing: number;
  T: number;
};

const SVG_STYLE: React.CSSProperties = { position: 'absolute', left: 0, bottom: 0, overflow: 'visible' };

const moodWeights = (mood: number) => {
  const sadness = clamp((0.45 - mood) / 0.35, 0, 1);
  const joy = clamp((mood - 0.75) / 0.35, 0, 1);
  return { sadness, joy, mouthK: -2.4 * sadness + 2.8 * joy, mouthO: Math.max(sadness, joy) };
};

const MomBody: React.FC<BodyProps & { family: Family; holding: boolean }> = ({ s, tint, mood, swing, family, holding }) => {
  const { sadness, joy, mouthK, mouthO } = moodWeights(mood);
  const Pz = (sad: string, neutral: string, happy: string) => posed(sad, neutral, happy, sadness, joy);
  const headTilt = sadness * 26 - joy * 9;
  const lean = sadness * 9;
  const fore = holding
    ? Pz('M31.5 63 C 29.5 58 28 54 27 49', 'M31 55 C 29 49 27 44 25 39', 'M32 25 C 31 20 29 16 27 11')
    : null;
  const hand = fore ? endPoint(fore) : null;
  return (
    <svg viewBox="0 0 100 130" width={s} height={s * 1.3} style={SVG_STYLE}>
      <ellipse cx="52" cy="72" rx="26" ry="40" fill={tint} opacity={WASH} />
      <ellipse cx="71" cy="50" rx="12" ry="16" fill={tint} opacity={WASH * 0.6} />
      <g fill={tint} opacity={WASH + 0.3}>
        <circle cx="86" cy="38" r="1.7" />
        <circle cx="92" cy="50" r="1" />
        <circle cx="12" cy="98" r="1.4" />
        <circle cx="90" cy="104" r="0.9" />
        <circle cx="80" cy="30" r="0.8" />
      </g>
      <g transform={`rotate(${lean} 51 62)`}>
        <g transform={`rotate(${headTilt} 52 31)`}>
          <path
            d="M39 12 C 40 5 55 0.5 62 6 C 68 11 69.5 20 66.5 27.5 C 64.5 25.5 61.5 25 59 25.5 C 59.5 20 58 15.5 55.5 13.5 C 50 14 44 13.5 39 12 Z"
            fill={INK}
          />
          {family === 2 && (
            <path d="M63 9 C 74 10 79 24 75 44 C 73 31 68 21 61.5 18 Z" fill={INK} transform={`rotate(${swing * 0.6} 63 12)`} />
          )}
          <Stroke d="M44.5 16 C 42.5 21 43.5 27 49 29.5" w={1.4} o={0.85} />
          <Stroke d={`M46.5 26 q2.5 ${mouthK.toFixed(2)} 5 0`} w={1.3} o={mouthO} />
          <g opacity={sadness}>
            <Stroke d="M70 20 l0 5" w={1.3} />
            <Stroke d="M74.5 25 l0 5" w={1.3} />
          </g>
          <g opacity={joy}>
            <Stroke d="M69 6 l3 -4" w={1.4} />
            <Stroke d="M74 12 l4.5 -2" w={1.4} />
            <Stroke d="M72 18 l4 1.5" w={1.4} />
          </g>
        </g>
        <Stroke d="M52 30.5 L51.5 36" w={2.2} />
        <Stroke d={Pz('M41 42 C 46 39.5 57 39.5 62 42', 'M41 39 C 46 36.5 57 36.5 62 39', 'M41 38 C 46 35 57 35 62 38')} w={2.8} />
        <Stroke
          d={Pz('M41 43.5 C 39.5 50 39.5 56 40.5 61', 'M41 40.5 C 39 48 39 55 40.5 61', 'M41 39.5 C 39 47 39 55 40.5 61')}
          w={3.4}
        />
        <Stroke
          d={Pz('M62 43.5 C 63.5 50 63.5 56 62 61', 'M62 40.5 C 63.5 48 63.5 55 62 61', 'M62 39.5 C 63.5 47 63.5 55 62 61')}
          w={2.2}
        />
        <Stroke d="M40 61.5 C 46 64.5 56 64.5 63 61.5" w={4} />
        {fore && hand ? (
          <g>
            <Stroke d={Pz('M41 44 C 37 50 34.5 56 32 62', 'M41 41 C 36 45 33 49 31 54', 'M41 40 C 37 36 34 31 32 26')} w={2.4} />
            <Stroke d={fore} w={2.2} />
            <rect
              x={hand[0] - 9.5}
              y={hand[1] - 14}
              width="8.5"
              height="14"
              rx="1.3"
              stroke={INK}
              strokeWidth="2"
              fill="none"
              transform={`rotate(-8 ${hand[0] - 5} ${hand[1] - 7})`}
            />
          </g>
        ) : (
          <Stroke
            d={Pz(
              'M41 44 C 38.5 52 37.5 62 37.5 74',
              `M41 41 C ${37 + swing * 0.25} 51 ${35 + swing * 0.4} 61 ${34 + swing * 0.5} 73`,
              'M41 40 C 34 34 28 27 24 19',
            )}
            w={2.4}
          />
        )}
        <Stroke
          d={Pz(
            'M62 44 C 64 52 64.5 62 63.5 74',
            `M62 41 C ${65 - swing * 0.2} 51 ${66 - swing * 0.35} 62 ${65 - swing * 0.45} 73`,
            'M62 41 C 68 47 68 56 62 61.5',
          )}
          w={2.4}
        />
      </g>
      <Stroke d="M41 63 C 38 76 36 90 34.5 101" w={4.2} />
      <Stroke d="M34 105 C 33.2 112 31.5 118 30 124" w={3.6} />
      <Stroke d="M50 65 C 48.5 84 46.5 102 44.5 122" w={1.8} o={0.85} />
      <Stroke d="M53 65 C 54.5 84 56.5 102 58.5 122" w={1.8} o={0.85} />
      <Stroke d="M62 63 C 65 76 67.5 90 69 101" w={4.2} />
      <Stroke d="M69.4 105 C 70.2 112 71.5 118 73 124" w={3.6} />
      <Stroke d="M30 124 L44.5 123" w={2} />
      <Stroke d="M58.5 123 L73 124" w={2} />
      <path d="M30 124.5 L44.5 123.5 L43.5 128 L27 129 Z" fill={INK} />
      <path d="M58.5 123.5 L73 124.5 L76 129 L59.5 128 Z" fill={INK} />
    </svg>
  );
};

const KidBody: React.FC<BodyProps & { kid: number; hop: number; balloon: boolean }> = ({ s, tint, mood, kid, hop, balloon, T }) => {
  const { sadness, joy, mouthK, mouthO } = moodWeights(mood);
  const Pz = (sad: string, neutral: string, happy: string) => posed(sad, neutral, happy, sadness, joy);
  const headTilt = sadness * 22 - joy * 7;
  const spread = joy * clamp(hop, 0, 1) * 6;
  const lfx = 43 - spread;
  const rfx = 57 + spread;
  const fy = 124 - spread * 0.3;
  const leftArm = Pz('M36.5 83 C 36.5 88 37 93 37.5 97', 'M35.5 81 C 34.5 86 33.5 91 33 95', 'M32 57 C 30 54 27 50 23 46');
  const rightArm = Pz('M63.5 83 C 63.5 88 63 93 62.5 97', 'M64.5 81 C 65.5 86 66.5 91 67 95', 'M68 57 C 70 54 73 50 77 46');
  /* The balloon is in the hand away from the mom, and floats outward: the
     kids stand either side of her (even to her left, odd to her right). */
  const out = kid % 2 === 0 ? -1 : 1;
  const [hx, hy] = endPoint(out < 0 ? leftArm : rightArm);
  const sway = Math.sin(T * 1.6 + kid * 1.7) * 3;
  /* Big enough to read three across (June: "make the balloons bigger"),
     on a string long enough to clear the kid's head. */
  const bx = hx + out * 13 + sway;
  const by = hy - 58;
  const balloonColor = BALLOONS[kid % 3];
  return (
    <svg viewBox="0 0 100 130" width={s} height={s * 1.3} style={SVG_STYLE}>
      <ellipse cx="50" cy="86" rx="20" ry="32" fill={tint} opacity={WASH} />
      <g transform={`rotate(${headTilt} 50 62)`}>
        <g transform="rotate(-8 50 46)">
          <path d="M35 43 C 33 26 46 15 58 18.5 C 70 22 72 38 68 48 C 66 40 60 35 52 36.5 C 45 38 40 40.5 35 43 Z" fill={INK} />
          {kid % 3 === 1 && (
            <g fill={INK}>
              <circle cx="32.5" cy="33" r="4.6" />
              <circle cx="70" cy="33" r="4.6" />
            </g>
          )}
          {kid % 3 === 2 && (
            <g fill={INK}>
              <circle cx="43" cy="17" r="5" />
              <circle cx="53" cy="13.5" r="5.5" />
              <circle cx="63" cy="18" r="5" />
            </g>
          )}
          <Stroke d="M36 45 C 37 54 43 60.5 50 60.5 C 55.5 60.5 60 57.5 63 52" w={1.5} o={0.9} />
          <Stroke d={`M45 54 q3 ${mouthK.toFixed(2)} 6 0`} w={1.4} o={mouthO} />
        </g>
        <g opacity={sadness}>
          <Stroke d="M72 42 l0 4.5" w={1.3} />
          <Stroke d="M76.5 47 l0 4.5" w={1.3} />
        </g>
        <g opacity={joy}>
          <Stroke d="M74 22 l3 -4" w={1.4} />
          <Stroke d="M79 29 l4.5 -1.5" w={1.4} />
          <Stroke d="M27 24 l-3 -4" w={1.4} />
        </g>
      </g>
      <Stroke d="M50 61 L50 66.5" w={2} />
      <Stroke d={Pz('M39 71.5 C 44 69 56 69 61 71.5', 'M39 69 C 44 66.5 56 66.5 61 69', 'M39 68 C 44 65.5 56 65.5 61 68')} w={2.8} />
      <Stroke d={Pz('M40 72.5 C 39 79 39 86 40 93', 'M40 70.5 C 39 78 39 86 40 93', 'M40 69.5 C 39 78 39 86 40 93')} w={2.4} />
      <Stroke d={Pz('M60 72.5 C 61 79 61 86 60 93', 'M60 70.5 C 61 78 61 86 60 93', 'M60 69.5 C 61 78 61 86 60 93')} w={2.4} />
      <Stroke d={Pz('M39 72 C 36.5 74 36 78 36.5 82', 'M39 70 C 35.5 72 34.5 76 35.5 80', 'M39 69 C 35 66 33 62 32 58')} w={2.4} />
      <Stroke d={Pz('M61 72 C 63.5 74 64 78 63.5 82', 'M61 70 C 64.5 72 65.5 76 64.5 80', 'M61 69 C 65 66 67 62 68 58')} w={2.4} />
      <Stroke d={leftArm} w={2.1} />
      <Stroke d={rightArm} w={2.1} />
      {balloon && (
        <g>
          <Stroke d={`M${hx} ${hy} C ${hx + out * 5} ${hy - 16} ${bx - out * 3} ${by + 34} ${bx} ${by + 20}`} w={1.1} o={0.9} />
          <path d={`M${bx - 3} ${by + 20.5} L${bx + 3} ${by + 20.5} L${bx} ${by + 17.5} Z`} fill={balloonColor} />
          <ellipse cx={bx} cy={by} rx="16" ry="19" fill={balloonColor} />
          <ellipse cx={bx - 5.5} cy={by - 6.5} rx="4" ry="6" fill={colors.white} opacity="0.55" />
        </g>
      )}
      <path d="M39 93 L61 93 L63 109 L52.5 109 L50 101 L47.5 109 L37 109 Z" fill={INK} />
      <Stroke d={`M44 109 L${lfx.toFixed(1)} ${fy.toFixed(1)}`} w={2.1} />
      <Stroke d={`M56 109 L${rfx.toFixed(1)} ${fy.toFixed(1)}`} w={2.1} />
      <path d={`M${lfx - 6} ${fy} L${lfx + 4} ${fy} L${lfx + 4.5} ${fy + 4} L${lfx - 8} ${fy + 4.5} Z`} fill={INK} />
      <path d={`M${rfx - 4} ${fy} L${rfx + 6} ${fy} L${rfx + 8} ${fy + 4.5} L${rfx - 4.5} ${fy + 4} Z`} fill={INK} />
    </svg>
  );
};

type CharacterProps = {
  T: number;
  role: 'mom' | 'kid';
  family: Family;
  kid?: number;
  size: number;
  /** How much they bounce: 0 still … 1 walking … 1.3 overjoyed. */
  energy: number;
  /** How fast: 1 as drawn, less for a slower step and bounce. */
  tempo?: number;
  phase?: number;
  mood: number;
  holding?: boolean;
  balloon?: boolean;
};

/** A figure standing on the ground at its anchor, hopping, squashing and
 *  tilting with its energy. */
const Character: React.FC<CharacterProps> = ({
  T,
  role,
  family,
  kid = 0,
  size: s,
  energy,
  tempo = 1,
  phase = 0,
  mood,
  holding = false,
  balloon = false,
}) => {
  const mom = role === 'mom';
  const tint = mom ? FAMILIES[family].mom : FAMILIES[family].kids[kid % 3];
  const w = T * tempo * (mom ? 5 : 6.5 + kid * 0.9) + phase;
  const hop = Math.abs(Math.sin(w));
  const amp = (mom ? 5 : 36) * energy;
  const sy = 1 + (mom ? 0.02 : 0.06) * Math.min(energy, 1.4) * Math.cos(2 * w);
  const tilt = (mom ? 1.2 : 7) * Math.sin(w) * Math.min(energy, 1.3);
  const swing = Math.sin(w - 0.6) * 9 * Math.min(energy, 1);
  const m = clamp(mood, 0, 1.2);
  return (
    <div style={{ position: 'absolute', left: -s / 2, bottom: 0, width: s, height: s * 1.3 }}>
      <div
        style={{
          position: 'absolute',
          left: s * 0.15,
          bottom: -7,
          width: s * 0.7,
          height: 14,
          borderRadius: '50%',
          background: colors.shadowSoft,
          transform: `scale(${1 - hop * 0.3 * Math.min(energy, 1)})`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          width: s,
          height: s * 1.3,
          transform: `translateY(${-hop * amp}px) rotate(${tilt}deg) scale(${1 / sy},${sy})`,
          transformOrigin: '50% 100%',
        }}
      >
        {mom ? (
          <MomBody s={s} tint={tint} mood={m} swing={swing} T={T} family={family} holding={holding} />
        ) : (
          <KidBody s={s} tint={tint} mood={m} swing={swing} T={T} kid={kid} hop={hop} balloon={balloon} />
        )}
      </div>
    </div>
  );
};

type FamilyGroupProps = {
  T: number;
  /** Where the mom stands, and the ground under them. */
  x: number;
  ground: number;
  family: Family;
  kids: number;
  size: number;
  energy: number;
  mood: number;
  holding?: boolean;
  balloon?: boolean;
  /** The way they face. They're drawn facing left; 'right' mirrors the
   *  whole family about the mom, phone hand and all. */
  facing?: 'left' | 'right';
  /** How fast they step and bounce: 1 as drawn. */
  tempo?: number;
};

const KID_KEYS = ['first', 'second', 'third'] as const;

/** A mom with her kids either side, anchored at (x, ground). */
export const FamilyGroup: React.FC<FamilyGroupProps> = ({
  T,
  x,
  ground,
  family,
  kids,
  size,
  energy,
  mood,
  holding,
  balloon,
  facing = 'left',
  tempo = 1,
}) => (
  <div
    style={{
      position: 'absolute',
      left: x,
      top: ground,
      width: 0,
      height: 0,
      transform: facing === 'right' ? 'scaleX(-1)' : undefined,
    }}
  >
    {KID_KEYS.slice(0, kids).map((key, i) => {
      const side = i % 2 ? 1 : -1;
      const off = side * (size * 0.6 + (i > 1 ? size * 0.4 : 0)) + Math.sin(T * tempo * 0.8 + i * 2.1) * size * 0.13 * Math.min(energy, 1);
      return (
        <div key={key} style={{ position: 'absolute', left: off, top: 0 }}>
          <Character
            T={T}
            role="kid"
            family={family}
            kid={i}
            size={size * 0.62}
            energy={energy}
            tempo={tempo}
            phase={i * 1.9 + 0.7}
            mood={mood}
            balloon={balloon}
          />
        </div>
      );
    })}
    <Character T={T} role="mom" family={family} size={size} energy={energy * 0.8} tempo={tempo} mood={mood} holding={holding} />
  </div>
);
